/**
 * BillingMethodsAdmin.tsx — Admin > Account > Billing: the practice's
 * subscriptions, the cards Prospera bills it with, and recent invoices.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): everything shows, and Recent
 * Invoices still opens, but there is no Add Payment Method, card capture,
 * facility change (the card's facility shows as plain text), Set Default or
 * Delete.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Star, Trash2, CreditCard } from 'lucide-react';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  deletePaymentMethod,
  getBillingOverview,
  setDefaultPaymentMethod,
  updatePaymentMethodFacility,
  type SubscribeBillingFacility,
  type SubscribeBillingInvoice,
  type SubscribeBillingOverview,
  type SubscribeBillingPaymentMethod,
  type SubscribeBillingSubscription,
} from '@/services/billingAdmin.service';
import { SubscribeBillingMethodCapture } from '@/components/payments/SubscribeBillingMethodCapture';

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  paused: 'bg-slate-100 text-slate-700',
  past_due: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
  canceled: 'bg-red-100 text-red-700',
  paid: 'bg-emerald-100 text-emerald-700',
  succeeded: 'bg-emerald-100 text-emerald-700',
  captured: 'bg-emerald-100 text-emerald-700',
  voided: 'bg-slate-100 text-slate-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-indigo-100 text-indigo-700',
  partially_refunded: 'bg-indigo-100 text-indigo-700',
  refund: 'bg-indigo-100 text-indigo-700',
};

function StatusBadge({ status }: { status: string }) {
  const key = status.toLowerCase();
  const cls = STATUS_CLASS[key] ?? 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${cls}`}>
      {status ? status.replace(/_/g, ' ') : '—'}
    </span>
  );
}

function formatCents(cents: number, interval?: string): string {
  const dollars = (cents / 100).toFixed(2);
  if (!interval) return `$${dollars}`;
  const suffix = interval === 'year' ? '/yr' : '/mo';
  return `$${dollars}${suffix}`;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} – ${end}`;
  return `${s.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })}`;
}

function facilityName(facilities: SubscribeBillingFacility[], id: number): string {
  if (!id) return 'Account-Level';
  const f = facilities.find((x) => x.facilityID === id);
  return f ? f.name : `Facility #${id}`;
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function BillingMethodsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [overview, setOverview] = useState<SubscribeBillingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SubscribeBillingPaymentMethod | null>(null);
  const [pendingDefault, setPendingDefault] = useState<SubscribeBillingPaymentMethod | null>(null);
  const [busyPmId, setBusyPmId] = useState<number | null>(null);
  // Local override of each card's facility dropdown before user clicks Save.
  const [facilityDraft, setFacilityDraft] = useState<Record<number, number>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBillingOverview();
      setOverview(data);
      // Reset drafts so they reflect the freshly loaded server state.
      const drafts: Record<number, number> = {};
      data.payment_methods.forEach((pm) => { drafts[pm.pm_id] = pm.facility_id; });
      setFacilityDraft(drafts);
    } catch (err) {
      toast.error('Failed to load billing methods', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const facilities = overview?.facilities ?? [];

  const handleSavedMethod = useCallback(() => {
    setAdding(false);
    toast.success('Billing method saved');
    load();
  }, [load]);

  async function handleSaveFacility(pm: SubscribeBillingPaymentMethod) {
    const next = facilityDraft[pm.pm_id] ?? 0;
    if (next === pm.facility_id) return;
    setBusyPmId(pm.pm_id);
    try {
      await updatePaymentMethodFacility(pm.pm_id, next);
      toast.success('Facility updated');
      await load();
    } catch (err) {
      toast.error('Failed to update facility', getErrorMessage(err));
    } finally {
      setBusyPmId(null);
    }
  }

  async function handleSetDefault(pm: SubscribeBillingPaymentMethod) {
    setBusyPmId(pm.pm_id);
    try {
      await setDefaultPaymentMethod(pm.pm_id);
      toast.success('Default billing method updated');
      await load();
    } catch (err) {
      toast.error('Failed to set default', getErrorMessage(err));
    } finally {
      setBusyPmId(null);
      setPendingDefault(null);
    }
  }

  async function handleDelete(pm: SubscribeBillingPaymentMethod) {
    setBusyPmId(pm.pm_id);
    try {
      await deletePaymentMethod(pm.pm_id);
      toast.success('Billing method removed');
      await load();
    } catch (err) {
      toast.error('Could not remove billing method', getErrorMessage(err));
    } finally {
      setBusyPmId(null);
      setPendingDelete(null);
    }
  }

  const activeSubscriptions = useMemo(
    () => (overview?.subscriptions ?? []).filter(
      (s) => s.status !== 'cancelled' && s.status !== 'canceled',
    ),
    [overview?.subscriptions],
  );

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Billing" />

      {loading ? (
        <p className="text-sm text-slate-500">Loading billing methods…</p>
      ) : (
        <>
          <SubscriptionsSection
            subscriptions={activeSubscriptions}
            facilities={facilities}
          />

            <PaymentMethodsSection
              paymentMethods={overview?.payment_methods ?? []}
              facilities={facilities}
              facilityDraft={facilityDraft}
              setFacilityDraft={setFacilityDraft}
              busyPmId={busyPmId}
              locked={locked}
              adding={adding}
              onAdd={() => setAdding(true)}
              onCancelAdd={() => setAdding(false)}
              onSavedMethod={handleSavedMethod}
              onSaveFacility={handleSaveFacility}
              onAskSetDefault={(pm) => setPendingDefault(pm)}
              onAskDelete={(pm) => setPendingDelete(pm)}
            />

            <RecentInvoicesSection invoices={overview?.recent_invoices ?? []} />
          </>
        )}

      <ConfirmDialog
        isOpen={!!pendingDefault}
        onClose={() => setPendingDefault(null)}
        onConfirm={() => { if (pendingDefault) handleSetDefault(pendingDefault); }}
        title="Set as default billing method?"
        variant="warning"
        confirmLabel="Set Default"
        message={pendingDefault ? (
          <p>
            Future recurring charges will go to{' '}
            <strong>{pendingDefault.brand || 'card'} •••• {pendingDefault.last4}</strong>.
            Any current default will be demoted.
          </p>
        ) : null}
      />

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => { if (pendingDelete) handleDelete(pendingDelete); }}
        title="Remove this billing method?"
        confirmLabel="Remove"
        message={pendingDelete ? (
          <p>
            <strong>{pendingDelete.brand || 'card'} •••• {pendingDelete.last4}</strong>{' '}
            will be soft-deleted. Methods attached to active, paused, or pending
            subscriptions cannot be removed.
          </p>
        ) : null}
      />
    </div>
  );
}

// ─── Sub-sections ───────────────────────────────────────────────────────────

interface SubscriptionsSectionProps {
  subscriptions: SubscribeBillingSubscription[];
  facilities: SubscribeBillingFacility[];
}

function SubscriptionsSection({ subscriptions, facilities }: SubscriptionsSectionProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-4 py-3">
        <h2 className="text-base font-semibold text-slate-900">Subscriptions</h2>
      </header>
      {subscriptions.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-slate-500">No subscriptions found for this account.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Facility</th>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Current Period</th>
                <th className="px-4 py-2">Last Charge</th>
                <th className="px-4 py-2">Next Charge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {subscriptions.map((s) => (
                <tr key={s.subscription_id}>
                  <td className="px-4 py-2 font-medium text-slate-900">#{s.subscription_id}</td>
                  <td className="px-4 py-2 text-slate-700">{facilityName(facilities, s.facility_id)}</td>
                  <td className="px-4 py-2 text-slate-700">{s.plan_name}</td>
                  <td className="px-4 py-2 font-semibold text-emerald-700">{formatCents(s.base_price_cents, s.billing_interval)}</td>
                  <td className="px-4 py-2"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-2 text-slate-700">{formatPeriod(s.current_period_start, s.current_period_end)}</td>
                  <td className="px-4 py-2 text-slate-700">{formatDate(s.last_charge_at)}</td>
                  <td className="px-4 py-2 text-slate-700">{formatDate(s.next_charge_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface PaymentMethodsSectionProps {
  paymentMethods: SubscribeBillingPaymentMethod[];
  facilities: SubscribeBillingFacility[];
  facilityDraft: Record<number, number>;
  setFacilityDraft: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  busyPmId: number | null;
  /** Locked page: no add, capture, facility change, Set Default or Delete. */
  locked: boolean;
  adding: boolean;
  onAdd: () => void;
  onCancelAdd: () => void;
  onSavedMethod: () => void;
  onSaveFacility: (pm: SubscribeBillingPaymentMethod) => void;
  onAskSetDefault: (pm: SubscribeBillingPaymentMethod) => void;
  onAskDelete: (pm: SubscribeBillingPaymentMethod) => void;
}

function PaymentMethodsSection({
  paymentMethods,
  facilities,
  facilityDraft,
  setFacilityDraft,
  busyPmId,
  locked,
  adding,
  onAdd,
  onCancelAdd,
  onSavedMethod,
  onSaveFacility,
  onAskSetDefault,
  onAskDelete,
}: PaymentMethodsSectionProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-base font-semibold text-slate-900">Payment Methods</h2>
        {!locked && (
          <Button onClick={onAdd} disabled={adding} variant="primary" size="sm">
            <Plus className="mr-1 h-4 w-4" /> Add Payment Method
          </Button>
        )}
      </header>

      <div className="space-y-3 p-4">
        {adding && !locked ? (
          <SubscribeBillingMethodCapture
            active={adding}
            facilities={facilities}
            onSaved={onSavedMethod}
            onCancel={onCancelAdd}
          />
        ) : null}

        {paymentMethods.length === 0 && !adding ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            {locked ? (
              'No billing methods on file.'
            ) : (
              <>
                No billing methods on file. Click <strong>Add Payment Method</strong> to capture one.
              </>
            )}
          </p>
        ) : null}

        {paymentMethods.map((pm) => {
              const draft = facilityDraft[pm.pm_id] ?? pm.facility_id;
              const facilityChanged = draft !== pm.facility_id;
              const busy = busyPmId === pm.pm_id;

              return (
                <div
                  key={pm.pm_id}
                  className={`rounded-lg border bg-white p-4 shadow-sm transition ${pm.is_default ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-slate-500" />
                      <span className="text-sm font-semibold text-slate-900">
                        {pm.brand || 'Card'} •••• {pm.last4 || '????'}
                      </span>
                      {pm.is_default ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          <Star className="h-3 w-3" /> Default
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500">
                      {pm.exp_month && pm.exp_year ? `Expires ${String(pm.exp_month).padStart(2, '0')}/${pm.exp_year}` : null}
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="text-xs">
                      <label className="mb-1 block font-medium text-slate-600">Billing Name</label>
                      <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                        {pm.billing_name || <span className="italic text-slate-400">Not specified</span>}
                      </div>
                    </div>

                    <div className="text-xs">
                      <label className="mb-1 block font-medium text-slate-600">Facility</label>
                      {locked ? (
                        // The card's facility as plain text, the way Billing Name shows.
                        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                          {pm.facility_id ? facilityName(facilities, pm.facility_id) : '— No Facility —'}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <select
                            value={draft}
                            disabled={busy}
                            onChange={(e) =>
                              setFacilityDraft((prev) => ({ ...prev, [pm.pm_id]: Number(e.target.value) }))
                            }
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          >
                            <option value={0}>— No Facility —</option>
                            {facilities.map((f) => (
                              <option key={f.facilityID} value={f.facilityID}>{f.name}</option>
                            ))}
                          </select>
                          {facilityChanged ? (
                            <Button size="sm" variant="primary" disabled={busy} onClick={() => onSaveFacility(pm)}>
                              Save
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>

                  {!locked && (
                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      {!pm.is_default ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => onAskSetDefault(pm)}
                        >
                          <Star className="mr-1 h-4 w-4" /> Set Default
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => onAskDelete(pm)}
                      >
                        <Trash2 className="mr-1 h-4 w-4" /> Delete
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
      </div>
    </section>
  );
}

interface RecentInvoicesSectionProps {
  invoices: SubscribeBillingInvoice[];
}

function RecentInvoicesSection({ invoices }: RecentInvoicesSectionProps) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => invoices.slice(0, 5), [invoices]);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <Chevron className="h-4 w-4 text-slate-400" />
        <h2 className="text-base font-semibold text-slate-900">Recent Invoices</h2>
        <span className="ml-auto text-xs text-slate-400">{rows.length} invoice{rows.length !== 1 ? 's' : ''}</span>
      </button>

      {open && (
        <>
          {rows.length === 0 ? (
            <p className="border-t border-slate-200 px-4 py-6 text-center text-sm text-slate-500">No invoices found.</p>
          ) : (
            <div className="overflow-x-auto border-t border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Invoice #</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Amount</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((i) => (
                    <tr key={i.invoice_id}>
                      <td className="px-4 py-2 font-medium text-slate-900">{i.invoice_number || `#${i.invoice_id}`}</td>
                      <td className="px-4 py-2 text-slate-700">{formatDate(i.issued_at)}</td>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-slate-900">${(i.total_cents / 100).toFixed(2)}</div>
                        {i.is_refunded && i.refunded_cents > 0 ? (
                          <div className="text-xs font-medium text-indigo-600">
                            −${(i.refunded_cents / 100).toFixed(2)} refunded
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2"><StatusBadge status={i.status} /></td>
                      <td className="px-4 py-2 text-slate-700">{formatPeriod(i.period_start, i.period_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
